import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Sparkles } from "lucide-react";
import CoupleAccent from "@/components/CoupleAccent";

export const ForgivenessSection = ({ onForgive }) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dodges, setDodges] = useState(0);
  const [forgiven, setForgiven] = useState(false);
  const wrapRef = useRef(null);

  const dodge = () => {
    const wrap = wrapRef.current?.getBoundingClientRect();
    if (!wrap) return;
    const maxX = Math.min(wrap.width / 2 - 80, 260);
    const maxY = 120;
    const nx = (Math.random() * 2 - 1) * maxX;
    const ny = (Math.random() * 2 - 1) * maxY;
    setPos({ x: nx, y: ny });
    setDodges((d) => d + 1);
  };

  const noMessages = [
    "no",
    "are you sure?",
    "please?",
    "just one yes?",
    "reconsider?",
    "pretty please?",
    "for me?",
    "eva…",
  ];

  const handleYes = () => {
    setForgiven(true);
    onForgive?.();
  };

  return (
    <section
      data-testid="forgiveness-section"
      className="relative py-24 sm:py-32 px-6 text-center"
    >
      <CoupleAccent
        src="/couple/nits_pleading.png"
        alt="nits with hands folded, asking for forgiveness"
        className="block mx-auto w-40 sm:w-48 mb-8"
        float={{ y: [0, -8, 0], rotate: [-2, 2, -2] }}
        duration={4.5}
      />
      <div className="max-w-xl mx-auto" ref={wrapRef}>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-hand text-2xl text-rose-400 mb-2"
        >
          one small question
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="font-serif-love text-4xl sm:text-5xl text-[#3A2C2C]"
        >
          do you forgive me?
        </motion.h2>

        <AnimatePresence mode="wait">
          {!forgiven ? (
            <motion.div
              key="buttons"
              exit={{ opacity: 0, y: -10 }}
              className="relative mt-12 h-40 flex items-center justify-center gap-6"
            >
              <motion.button
                data-testid="forgive-yes-button"
                onClick={handleYes}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.96 }}
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ scale: { repeat: Infinity, duration: 2.4, ease: "easeInOut" } }}
                className="px-10 py-4 rounded-full bg-[#D4A3A3] hover:bg-[#C28080] text-white font-medium text-lg shadow-xl shadow-rose-200"
              >
                yes, always
              </motion.button>

              <motion.button
                data-testid="forgive-no-button"
                onMouseEnter={dodge}
                onFocus={dodge}
                onClick={dodge}
                animate={{ x: pos.x, y: pos.y }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className="px-6 py-3 rounded-full border border-[#E8D8D8] bg-white/70 backdrop-blur text-[#7D6B6B] text-sm"
              >
                {noMessages[Math.min(dodges, noMessages.length - 1)]}
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              key="forgiven"
              data-testid="forgiven-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="mt-12 flex flex-col items-center gap-4"
            >
              <div className="flex items-center gap-2 text-[#C28080]">
                <Sparkles className="w-5 h-5" />
                <Heart className="w-6 h-6" fill="currentColor" strokeWidth={0} />
                <Sparkles className="w-5 h-5" />
              </div>
              <p className="font-serif-love text-3xl sm:text-4xl italic text-[#C28080]">
                thank you, eva.
              </p>
              <p className="font-body-love text-[#7D6B6B] max-w-md">
                i don't take it lightly. i'll keep earning it — every single day.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {dodges > 2 && !forgiven && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="font-hand text-xl text-[#C28080] mt-6"
          >
            (there's only one right button, my love)
          </motion.p>
        )}
      </div>
    </section>
  );
};

export default ForgivenessSection;
